import { useState, type CSSProperties } from "react";
import { isActiveAdminLesson } from "@/components/radar/utils/activeAdminLessons";
import { useTalkT } from "./useTalkT";

export const TALK_RADAR_DOCK_HEIGHT = 80;

export type StornoConfirm =
  | { kind: "lesson"; lessonId: string }
  | { kind: "request" }
  | { kind: "remind" };

export function TalkRadarLessonStorno({
  status,
  onAsk,
  filled = false,
}: {
  status?: string | null;
  onAsk: () => void;
  filled?: boolean;
}) {
  const t = useTalkT();
  if (!isActiveAdminLesson(status)) return null;
  return (
    <button
      type="button"
      onClick={onAsk}
      style={
        filled
          ? { background: "#dc2626", color: "#fff", fontWeight: 700, border: "none" }
          : undefined
      }
    >
      {t("talk_radar_storno")}
    </button>
  );
}

function dockButton(background: string): CSSProperties {
  return {
    background,
    color: "#fff",
    fontWeight: 800,
    fontSize: 13,
    border: "none",
    borderRadius: 12,
    minHeight: 52,
    width: "100%",
  };
}

export function TalkRadarDock({
  showCancel,
  onRemind,
  onCancelRequest,
  onCancelLessons,
}: {
  showCancel: boolean;
  onRemind: () => void;
  onCancelRequest: () => void;
  onCancelLessons: () => void;
}) {
  const t = useTalkT();
  return (
    <div
      role="group"
      aria-label={t("talk_radar_actions")}
      style={{
        flex: "0 0 auto",
        display: "grid",
        gridTemplateColumns: showCancel ? "1fr 1fr 1fr" : "1fr 1fr",
        gap: 8,
        boxSizing: "border-box",
        minHeight: TALK_RADAR_DOCK_HEIGHT,
        padding: "10px 12px 18px",
        background: "#081018",
        borderTop: "1px solid #334155",
      }}
    >
      <button type="button" style={dockButton("#d97706")} onClick={onRemind}>
        {t("talk_radar_remind")}
      </button>
      <button
        type="button"
        aria-label={t("talk_radar_storno_request")}
        style={dockButton("#2563eb")}
        onClick={onCancelRequest}
      >
        {t("talk_radar_storno_request_short")}
      </button>
      {showCancel && (
        <button type="button" style={dockButton("#dc2626")} onClick={onCancelLessons}>
          {t("talk_radar_storno")}
        </button>
      )}
    </div>
  );
}

export function TalkRadarRequestStorno({
  onRemind,
  onCancel,
}: {
  onRemind: () => void;
  onCancel: () => void;
}) {
  const t = useTalkT();
  return (
    <div>
      <button type="button" onClick={onRemind}>
        {t("talk_radar_remind")}
      </button>
      <button type="button" onClick={onCancel}>
        {t("talk_radar_storno_request")}
      </button>
    </div>
  );
}

export function TalkRadarStornoConfirm({
  confirm,
  lastLive,
  onAbort,
  onCommit,
}: {
  confirm: StornoConfirm;
  lastLive?: boolean;
  onAbort: () => void;
  onCommit: (message: string) => void;
}) {
  const t = useTalkT();
  const [message, setMessage] = useState("");
  const copy =
    confirm.kind === "lesson"
      ? t("talk_radar_storno_lesson_ask")
      : confirm.kind === "request"
        ? t("talk_radar_storno_request_ask")
        : t("talk_radar_remind_ask");
  const askButton: CSSProperties = {
    minHeight: 44,
    marginRight: 8,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#111827",
    color: "#e8eef9",
    fontWeight: 700,
  };
  return (
    <section style={{ marginTop: 16, color: "#e8eef9" }}>
      <p>{copy}</p>
      {lastLive && confirm.kind === "lesson" && <p>{t("talk_radar_last_live")}</p>}
      {confirm.kind === "remind" && (
        <textarea
          aria-label={t("talk_radar_remind")}
          value={message}
          placeholder={t("talk_radar_remind_placeholder")}
          onChange={(event) => setMessage(event.target.value)}
          style={{
            width: "100%",
            minHeight: 72,
            marginBottom: 8,
            borderRadius: 12,
            border: "1px solid #334155",
            background: "#111827",
            color: "#e8eef9",
          }}
        />
      )}
      <button type="button" style={askButton} onClick={() => onCommit(message)}>
        {t("talk_radar_confirm")}
      </button>
      <button type="button" style={askButton} onClick={onAbort}>
        {t("talk_radar_cancel")}
      </button>
    </section>
  );
}
